/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback } from "react";
import { Else, If, Then, When } from "react-if";
import { FlexContainer, Tab, Tabs as TabContainer } from "@nordcloud/gnui";
import { ActionScope } from "~/generated/graphql";
import { useActionTabNavigation } from "~/hooks";
import { isNotEmpty } from "~/tools";
import { RedIndicator } from "~/views/plans/PlanDetails/Tabs/DetailsTab/components/ActionBox/components/RedIndicatior";
import { TabIcon } from "~/views/plans/PlanDetails/Tabs/DetailsTab/components/ActionBox/components/TabIcon";
import { PlanErrors, PlanField } from "../../constants";
import { usePlanWizard } from "../../PlanProvider";
import { useStepState } from "../StepProvider";
import { ActionResources } from "./ActionResources";
import { ActionSettingsForm } from "./ActionSettingsForm";
import { BatchSettingsForm } from "./ActionSettingsForm/BatchSettingsForm";
import { AdvancedSettingsForm } from "./AdvancedSettingsForm";
import { NotificationForm } from "./NotificationForm";

type Props = {
  nextStep?: () => void;
};

const ACTION_TAB_STEP_MAPPING: Record<string, number> = {
  general: 0,
  advanced: 1,
  notifications: 2,
};

export function ActionView({ nextStep }: Props) {
  const { currentStep, handleTab: handleActionTab } = useActionTabNavigation(
    ACTION_TAB_STEP_MAPPING
  );
  const {
    stepState: { selectedEntity },
    updateStepState,
  } = useStepState();

  const { planData } = usePlanWizard();

  const handleTab = useCallback(
    (step: number) => {
      handleActionTab(step);
      updateStepState({
        selectedEntity: { ...selectedEntity, selectedTab: step },
      });
    },
    [selectedEntity, updateStepState, handleActionTab]
  );
  const isError = (planData?.[PlanField.ERRORS] ?? []).includes(
    PlanErrors.PLAN_ACTION_SETTINGS
  );
  const isBatch = isNotEmpty(selectedEntity?.batchActions ?? []);

  const shouldDisplayResourcesSection =
    selectedEntity?.scope === ActionScope.Resource;

  return (
    <>
      <When condition={shouldDisplayResourcesSection}>
        <ActionResources />
      </When>
      <FlexContainer
        css={{ flex: 1, minWidth: "20rem", height: "100%" }}
        alignItems="stretch"
      >
        <If condition={!isBatch}>
          <Then>
            <TabContainer step={currentStep} handleTab={handleTab}>
              <Tab
                label={
                  <RedIndicator isVisible={isError}>
                    <TabIcon
                      caption="General Action Settings"
                      icon="settings"
                    />
                  </RedIndicator>
                }
                width="auto"
                css={{ minHeight: "13rem" }}
              >
                <ActionSettingsForm {...{ nextStep }} setTab={handleTab} />
              </Tab>
              <Tab
                label={
                  <TabIcon
                    caption="Advanced Action Settings"
                    icon="doubleGear"
                  />
                }
                width="auto"
                css={{ minHeight: "13rem" }}
              >
                <AdvancedSettingsForm {...{ nextStep }} setTab={handleTab} />
              </Tab>
              <Tab
                label={<TabIcon caption="Notification Settings" icon="bell" />}
                width="auto"
                css={{ minHeight: "13rem" }}
              >
                <NotificationForm {...{ nextStep }} />
              </Tab>
            </TabContainer>
          </Then>
          <Else>
            <TabContainer step={0} handleTab={handleTab}>
              <Tab
                label={
                  <RedIndicator isVisible={isError}>
                    <TabIcon caption="Action Batch Settings" icon="settings" />
                  </RedIndicator>
                }
                width="auto"
                css={{ minHeight: "13rem" }}
              >
                <BatchSettingsForm {...{ nextStep }} />
              </Tab>
            </TabContainer>
          </Else>
        </If>
      </FlexContainer>
    </>
  );
}
